import React from 'react';
import { Popover, Typography, Button } from 'antd';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Spinner } from '../../components/Spinner';
import { useExplainPrediction } from '../../core/modules/book/hooks/useExplainPrediction';
import { ReactComponent as SparkleSvg } from '../../assets/icons/sparkles.svg';

const { Paragraph, Text } = Typography;

const ACCENT = '#722ed1';

const Sparkle = (props: Record<string, unknown>) => <Icon component={SparkleSvg} {...props} />;

interface IPredictionExplanationProps {
  book: object;
}

const toNumber = (value: unknown): number | null => {
  const num = Number(value);

  return Number.isFinite(num) ? num : null;
};

// Render **bold** segments as accented <strong> (text nodes only — no HTML injection).
const BOLD_RE = /(\*\*[^*]+\*\*)/g;
const BOLD_ONE = /^\*\*[^*]+\*\*$/;

const renderInline = (text: string) =>
  text.split(BOLD_RE).map((part, i) =>
    (BOLD_ONE).test(part)
      ? <strong key={i} style={{ color: ACCENT }}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );

// Turn the model's light markup (summary line + "- " bullets) into clean UI.
const renderExplanation = (text: string) => {
  const lines = text
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);
  const bullets = lines.filter(l => l.startsWith('- ') || l.startsWith('* '));
  const paras = lines.filter(l => !(l.startsWith('- ') || l.startsWith('* ')));

  return (
    <>
      {paras.map((p, i) => (
        <Paragraph key={`p${i}`} style={{ marginBottom: bullets.length ? 8 : 0 }}>
          {renderInline(p)}
        </Paragraph>
      ))}
      {bullets.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {bullets.map((b, i) => (
            <li key={`b${i}`} style={{ marginBottom: 4 }}>{renderInline(b.slice(2))}</li>
          ))}
        </ul>
      )}
    </>
  );
};

const PredictionExplanation: React.FC<IPredictionExplanationProps> = ({ book }) => {
  const { mutate, data, isLoading, isError } = useExplainPrediction();

  const predictedUnits = toNumber(book['Projected Sales']);
  const successProbability = toNumber(book['Confidence']);

  // No prediction to explain (e.g. Royal Road rows without a model score).
  if (predictedUnits === null || successProbability === null) {
    return null;
  }

  const generate = () => {
    mutate({
      amazonasin: book['ASIN'],
      title: book['Title'],
      author: book['Author'],
      genre: book['Genre'] ?? null,
      success_probability: successProbability,
      predicted_units: predictedUnits
    });
  };

  // Generate on the icon click (version-independent — does not rely on the
  // Popover open callback, whose prop name differs across antd versions).
  const handleClick = () => {
    if (!data && !isLoading) {
      generate();
    }
  };

  const content = (
    <div style={{ width: 340 }}>
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spinner size='default' spinning indicator={<LoadingOutlined style={{ fontSize: 22 }} />} />
          <div style={{ marginTop: 10 }}>
            <Text type='secondary' style={{ fontSize: 12 }}>Analysing the prediction & book metadata…</Text>
          </div>
        </div>
      )}
      {isError && !isLoading && (
        <Text type='danger'>Unable to generate summary right now. Please try again later.</Text>
      )}
      {data && !isLoading && (
        <>
          <div
            style={{
              marginBottom: 0,
              fontSize: 13,
              lineHeight: 1.6,
              color: '#3a3a3a',
              maxHeight: 720,
              overflowY: 'auto'
            }}
          >
            {renderExplanation(data.explanation)}
          </div>
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
            <Text type='secondary' style={{ fontSize: 11 }}>
              <Sparkle style={{ marginRight: 4 }} />AI-generated from Brenda&apos;s model signals
            </Text>
          </div>
        </>
      )}
      {!isLoading && !isError && !data && (
        <Text type='secondary' style={{ fontSize: 12 }}>Analysing the prediction…</Text>
      )}
    </div>
  );

  return (
    <Popover
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', fontWeight: 600 }}>
          <Sparkle style={{ marginRight: 10, color: ACCENT }} />Analyzed with AI
        </span>
      }
      trigger='click'
      placement='bottomRight'
      content={content}
      overlayStyle={{ maxWidth: 380 }}
    >
      <Button
        type='text'
        shape='circle'
        onClick={handleClick}
        aria-label='Analyze with AI'
        title='Analyze with AI'
        icon={<Sparkle style={{ fontSize: 20, color: ACCENT }} />}
        style={{
          width: 32,
          height: 32,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    </Popover>
  );
};

export default PredictionExplanation;
